import { API_URL } from "utils/constants";

import { RealtimeConnectionState, RealtimeSignal, RealtimeTopic } from "./types";

const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
const JITTER_RATIO = 0.25;
const CLOSE_UNAUTHORIZED = 4401;

type SocketFactory = (url: string) => WebSocket;

type Options = {
  socketFactory?: SocketFactory;
};

export function toWebSocketUrl(apiUrl: string | undefined): string {
  const base = (apiUrl || "").replace(/\/+$/, "");
  const swapped = base.replace(/^http/, "ws");
  return `${swapped}/api/ws`;
}

export class RealtimeClient {
  private socket: WebSocket | null = null;
  private token = "";
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private currentState: RealtimeConnectionState = "idle";
  private hadReadyConnection = false;
  private hidden = false;
  private bufferedTopics = new Set<RealtimeTopic | "*">();

  private readonly signalHandlers = new Set<(signal: RealtimeSignal) => void>();
  private readonly stateHandlers = new Set<(state: RealtimeConnectionState) => void>();
  private readonly socketFactory: SocketFactory;

  constructor(options: Options = {}) {
    this.socketFactory = options.socketFactory ?? ((url) => new WebSocket(url));
  }

  get state(): RealtimeConnectionState {
    return this.currentState;
  }

  subscribe(handler: (signal: RealtimeSignal) => void): () => void {
    this.signalHandlers.add(handler);
    return () => {
      this.signalHandlers.delete(handler);
    };
  }

  onStateChange(handler: (state: RealtimeConnectionState) => void): () => void {
    this.stateHandlers.add(handler);
    return () => {
      this.stateHandlers.delete(handler);
    };
  }

  start(token: string): void {
    if (this.token === token && this.currentState !== "idle" && this.currentState !== "stopped") return;
    this.token = token;
    this.attempt = 0;
    this.hadReadyConnection = false;
    this.connect();
  }

  stop(): void {
    this.clearTimer();
    this.setState("idle");
    this.token = "";
    this.bufferedTopics.clear();
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }
  }

  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    if (hidden) return;

    const buffered = Array.from(this.bufferedTopics);
    this.bufferedTopics.clear();
    buffered.forEach((topic) => this.broadcast({ topic, scope: {} } as RealtimeSignal));
  }

  private connect(): void {
    this.setState(this.hadReadyConnection ? "reconnecting" : "connecting");

    const socket = this.socketFactory(toWebSocketUrl(API_URL));
    this.socket = socket;

    socket.onopen = () => {
      this.setState("authenticating");
      socket.send(JSON.stringify({ type: "auth", token: this.token }));
    };

    socket.onmessage = (event: MessageEvent) => {
      this.handleMessage(String(event.data));
    };

    socket.onclose = (event: CloseEvent) => {
      this.socket = null;
      if (event.code === CLOSE_UNAUTHORIZED) {
        this.setState("stopped");
        return;
      }
      this.setState("reconnecting");
      this.scheduleReconnect();
    };

    socket.onerror = () => {
      socket.close();
    };
  }

  private handleMessage(raw: string): void {
    let message: { type?: string; topic?: RealtimeTopic; scope?: Record<string, number> };
    try {
      message = JSON.parse(raw);
    } catch {
      return;
    }

    if (message.type === "ready") {
      this.attempt = 0;
      this.setState("ready");
      if (this.hadReadyConnection) {
        this.broadcast({ topic: "*", scope: {} });
      }
      this.hadReadyConnection = true;
      return;
    }

    if (message.type === "ping") {
      this.socket?.send(JSON.stringify({ type: "pong" }));
      return;
    }

    if (message.type === "resync") {
      this.broadcast({ topic: "*", scope: {} });
      return;
    }

    if (message.type === "event" && message.topic) {
      this.broadcast({ topic: message.topic, scope: message.scope ?? {} });
    }
  }

  private broadcast(signal: RealtimeSignal): void {
    if (this.hidden) {
      this.bufferedTopics.add(signal.topic);
      return;
    }
    this.signalHandlers.forEach((handler) => handler(signal));
  }

  private scheduleReconnect(): void {
    this.clearTimer();
    const base = BACKOFF_STEPS_MS[Math.min(this.attempt, BACKOFF_STEPS_MS.length - 1)];
    this.attempt += 1;
    const jitter = base * JITTER_RATIO * Math.random();
    this.timer = setTimeout(() => this.connect(), base + jitter);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private setState(next: RealtimeConnectionState): void {
    if (this.currentState === next) return;
    this.currentState = next;
    this.stateHandlers.forEach((handler) => handler(next));
  }
}

export const realtimeClient = new RealtimeClient();
